/**
 * The quick-note panel's state (feedback quick capture spec 01 §2): the draft note, its screenshot and images, and
 * the send. Headless: the panel in the web shell draws it.
 *
 * Opening the panel calls `begin()`, which draws the page; a failed drawing leaves no screenshot and says so, and
 * never blocks Send. A send that works clears the note, takes a fresh screenshot and leaves a "Sent" line; one that
 * fails keeps everything and says why.
 */
import { useCallback, useRef, useState } from "react";
import { newRequestId } from "./request-id";
import { useFeedbackCapture } from "../capabilities/feedback-capture";
import { usePlatformCapabilities } from "../capabilities/context";
import { getRuntimeConfig } from "../config/runtime-config";
import { useDataAdapter } from "../data/DataAdapterContext";
import { ApiError } from "../data/api/errors";
import { getRecentApiFailures } from "../data/api/recent-errors";
import {
  describeSendFailure,
  imageRefusal,
  quickNoteRequest,
  recentErrorsOf,
  sentNoteLink,
  type QuickNoteCapture,
} from "./feedback-quick-note";

export type ScreenshotState =
  | { status: "rendering" }
  | { status: "ready"; blob: Blob }
  /** The person removed it. */
  | { status: "removed" }
  | { status: "failed"; reason: string };

export interface NoteImage {
  id: string;
  blob: Blob;
  name: string;
}

export type SendState =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "failed"; message: string }
  | { status: "sent"; link: { href: string; isExternal: boolean } | null };

export interface QuickNote {
  text: string;
  screenshot: ScreenshotState;
  images: NoteImage[];
  sendStatus: SendState;
  /** What was captured about the page; null until the panel has been opened once. */
  capture: QuickNoteCapture | null;
  /** Why the last image the person tried to add was refused, or null. */
  imageNotice: string | null;
  canSend: boolean;
  setText(text: string): void;
  /** Open (or reopen after a send): draw the page and note where it was. */
  begin(): void;
  /** Draw the page again, without the panel. */
  retakeScreenshot(): void;
  removeScreenshot(): void;
  addImages(files: readonly Blob[]): void;
  removeImage(id: string): void;
  send(): Promise<void>;
}

function extensionOf(type: string): string {
  return type === "image/jpeg" ? "jpg" : type === "image/webp" ? "webp" : "png";
}

export function useQuickNote(options: { previewId: string | null }): QuickNote {
  const adapter = useDataAdapter();
  const platform = usePlatformCapabilities();
  const feedbackCapture = useFeedbackCapture();
  const [text, setTextState] = useState("");
  const [screenshot, setScreenshot] = useState<ScreenshotState>({
    status: "removed",
  });
  const [images, setImages] = useState<NoteImage[]>([]);
  const [send, setSend] = useState<SendState>({ status: "idle" });
  const [capture, setCapture] = useState<QuickNoteCapture | null>(null);
  const [imageNotice, setImageNotice] = useState<string | null>(null);

  // One attempt keeps one request id and its uploaded images, so a resend after a lost answer cannot double the note.
  const requestId = useRef(newRequestId());
  const uploaded = useRef(new Map<Blob, string>());
  const drawing = useRef(0);
  const nextImageId = useRef(0);

  const changed = useCallback(() => {
    requestId.current = newRequestId();
    setSend((current) =>
      current.status === "sending" ? current : { status: "idle" },
    );
  }, []);

  const describePage = useCallback((): QuickNoteCapture => {
    const config = getRuntimeConfig();
    const { width, height } = platform.getViewportSize();
    return {
      environment: config.environment,
      buildSha: config.appVersion,
      previewId: options.previewId,
      pagePath: platform.getCurrentPath(),
      viewport: `${width}x${height}`,
    };
  }, [platform, options.previewId]);

  const drawPage = useCallback(() => {
    const attempt = ++drawing.current;
    setCapture(describePage());
    setScreenshot({ status: "rendering" });
    feedbackCapture.captureScreenshot().then(
      (blob) => {
        if (attempt !== drawing.current) return;
        const refusal = imageRefusal(blob, 0);
        setScreenshot(
          refusal === null
            ? { status: "ready", blob }
            : { status: "failed", reason: refusal },
        );
      },
      (err: unknown) => {
        if (attempt !== drawing.current) return;
        console.warn("feedback.quick_note.screenshot_failed", {
          exc_type: err instanceof Error ? err.name : typeof err,
        });
        setScreenshot({
          status: "failed",
          reason: "The page could not be drawn.",
        });
      },
    );
  }, [describePage, feedbackCapture]);

  const setText = useCallback(
    (next: string) => {
      setTextState(next);
      changed();
    },
    [changed],
  );

  const addImages = useCallback(
    (files: readonly Blob[]) => {
      let notice: string | null = null;
      const added: NoteImage[] = [];
      for (const blob of files) {
        // Counted with what is already on the note and what this paste has added, plus the screenshot.
        const onNote =
          images.length +
          added.length +
          (screenshot.status === "ready" ? 1 : 0);
        const refusal = imageRefusal(blob, onNote);
        if (refusal) {
          notice = refusal;
          continue;
        }
        nextImageId.current += 1;
        added.push({
          id: `image-${nextImageId.current}`,
          blob,
          name: `pasted-${nextImageId.current}.${extensionOf(blob.type)}`,
        });
      }
      setImageNotice(notice);
      if (added.length > 0) {
        setImages((current) => [...current, ...added]);
        changed();
      }
    },
    [images.length, screenshot.status, changed],
  );

  const removeImage = useCallback(
    (id: string) => {
      setImages((current) => current.filter((image) => image.id !== id));
      setImageNotice(null);
      changed();
    },
    [changed],
  );

  const removeScreenshot = useCallback(() => {
    drawing.current += 1;
    setScreenshot({ status: "removed" });
    changed();
  }, [changed]);

  const retakeScreenshot = useCallback(() => {
    drawPage();
    changed();
  }, [drawPage, changed]);

  const sendNote = useCallback(async () => {
    if (!text.trim() || send.status === "sending" || !capture) return;
    setSend({ status: "sending" });
    try {
      const toUpload: { blob: Blob; name: string }[] = [
        ...(screenshot.status === "ready"
          ? [{ blob: screenshot.blob, name: "screenshot.png" }]
          : []),
        ...images.map(({ blob, name }) => ({ blob, name })),
      ];
      // One at a time: the first call may open the sign-in popup, and two must not.
      const imageIds: string[] = [];
      for (const { blob, name } of toUpload) {
        let imageId = uploaded.current.get(blob);
        if (imageId === undefined) {
          imageId = (await adapter.uploadFeedbackImage(blob, name)).image_id;
          uploaded.current.set(blob, imageId);
        }
        imageIds.push(imageId);
      }
      const result = await adapter.sendQuickNote(
        quickNoteRequest({
          note: text,
          imageIds,
          capture,
          recentErrors: recentErrorsOf(
            getRecentApiFailures(),
            feedbackCapture.recentConsoleErrors(),
          ),
          requestId: requestId.current,
        }),
      );
      const link = sentNoteLink(
        result.created,
        getRuntimeConfig().feedbackHubApiBaseUrl,
      );
      setTextState("");
      setImages([]);
      setImageNotice(null);
      uploaded.current = new Map();
      requestId.current = newRequestId();
      drawPage();
      setSend({ status: "sent", link });
    } catch (err) {
      console.warn("feedback.quick_note.send_failed", {
        exc_type: err instanceof Error ? err.name : typeof err,
        status: err instanceof ApiError ? err.status : null,
      });
      setSend({ status: "failed", message: describeSendFailure(err) });
    }
  }, [
    text,
    send.status,
    capture,
    screenshot,
    images,
    adapter,
    feedbackCapture,
    drawPage,
  ]);

  return {
    text,
    screenshot,
    images,
    sendStatus: send,
    capture,
    imageNotice,
    canSend: text.trim().length > 0 && send.status !== "sending",
    setText,
    begin: drawPage,
    retakeScreenshot,
    removeScreenshot,
    addImages,
    removeImage,
    send: sendNote,
  };
}
